import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw, Search, Filter, Download, Zap, CloudRain, Activity, Settings } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import SoilingLossChart from '../charts/SoilingLossChart';
import InverterClippingChart from '../charts/InverterClippingChart';
import InverterBreakdownChart from '../charts/InverterBreakdownChart';
import InverterEfficiencyChart from '../charts/InverterEfficiencyChart';
import InverterEfficiencyHeatmap from '../charts/InverterEfficiencyHeatmap';
import moment from 'moment';
import DatePickerButton from '../ui/date-picker-button';
import useDailySummary from '../../hooks/use-daily-summary';

const AssetManagement = () => {
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
  const [chartStartDate, setChartStartDate] = useState(yesterday);
  const [chartEndDate, setChartEndDate] = useState(yesterday);
  // Insert activeTab state for tab navigation
  const [activeTab, setActiveTab] = useState('inverter');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { summary: dailySummary } = useDailySummary();

  // Asset type mapping
  const assetTypes = {
    INV: { name: 'Inverter', icon: Zap, color: 'blue' },
    WS: { name: 'Weather Sensor', icon: CloudRain, color: 'green' },
    TRAFO: { name: 'Transformer', icon: Activity, color: 'purple' },
    CB: { name: 'Circuit Breaker', icon: Settings, color: 'orange' }
  };

  useEffect(() => {
    fetchAssets();
    // Re-fetch assets whenever date range changes to keep list in sync
  }, [chartStartDate, chartEndDate]);

  const fetchAssets = async () => {
    try {
      setIsLoading(true);

      // Build date range (yesterday by default)
      const start = moment(chartStartDate).format('YYYY-MM-DD');
      const end = moment(chartEndDate).format('YYYY-MM-DD');

      // Fetch energy loss data for the plant (aggregated)
      const res = await apiService.getForEnergyLoss({
        start_date: `${start} 00:00:00`,
        end_date: `${end} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });

      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      const processedAssets = [];

      if (Array.isArray(arr) && arr.length > 0) {
        const eya = arr[0]?.readings?.[0]?.EYA || {};

        // Derive Inverter IDs from clipping map
        const inverterIds = Object.keys(eya.inverter_clipping_loss_list_percentage || {});
        inverterIds.forEach(id => {
          processedAssets.push({
            asset_id: id,
            display_name: id,
            type: 'INV',
            make: '-',
            model: '-',
            capacity: '-',
            availability: 100,
            typeInfo: assetTypes['INV'],
            status: 'operational'
          });
        });

        // Derive Weather Sensor IDs from soiling map
        const wmsIds = Object.keys(eya.soiling_loss_wms_level || {});
        wmsIds.forEach(id => {
          processedAssets.push({
            asset_id: id,
            display_name: id,
            type: 'WS',
            make: '-',
            model: '-',
            capacity: '-',
            availability: 100,
            typeInfo: assetTypes['WS'],
            status: 'operational'
          });
        });
      }

      setAssets(processedAssets);
    } catch (error) {
      console.error('Error fetching assets:', error);
      // Silently fail without user-facing toast
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         asset.asset_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || asset.type === filterType;
    return matchesSearch && matchesFilter;
  });

  // Lists of Inverter and Weather Sensor IDs for chart queries
  const inverterIds = assets.filter(a => a.type === 'INV').map(a => a.asset_id);
  const wmsIds = assets.filter(a => a.type === 'WS').map(a => a.asset_id);

  const handleAssetClick = (asset) => {
    if (asset.type === 'INV') {
      navigate('/assetpage/inverter', { state: { assetId: asset.asset_id } });
    } else if (asset.type === 'WS') {
      navigate('/assetpage/weathersensor', { state: { assetId: asset.asset_id } });
    } else {
      toast({
        title: "Info",
        description: `${asset.typeInfo.name} management coming soon`,
      });
    }
  };

  const exportAssets = () => {
    const headers = ['Asset ID', 'Display Name', 'Type', 'Make', 'Model', 'Capacity', 'Status'];
    const csvRows = [headers.join(',')];
    
    filteredAssets.forEach(asset => {
      csvRows.push([
        asset.asset_id,
        asset.display_name,
        asset.typeInfo.name,
        asset.make || '-',
        asset.model || '-',
        asset.capacity || '-',
        asset.status
      ].join(','));
    });
    
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assets-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: "Success",
      description: "Asset data exported successfully"
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Asset Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Monitor and manage plant assets
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={exportAssets}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={fetchAssets} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search assets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700"
              >
                <option value="all">All Types</option>
                {Object.entries(assetTypes).map(([key, type]) => (
                  <option key={key} value={key}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Asset Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(assetTypes).map(([key, type]) => {
          const count = assets.filter(a => a.type === key).length;
          const Icon = type.icon;
          return (
            <Card key={key}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{type.name}s</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={`p-3 rounded-lg bg-${type.color}-100 dark:bg-${type.color}-900/20`}>
                    <Icon className={`h-6 w-6 text-${type.color}-600 dark:text-${type.color}-400`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Efficiency Metrics (from Daily Summary) */}
      {dailySummary && (
        <Card>
          <CardHeader>
            <CardTitle>Efficiency Metrics (Daily)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { label: 'Inverter Availability (%)', val: dailySummary.inverter_availability },
                { label: 'Soiling Loss (%)', val: dailySummary.soiling_loss },
                { label: 'Total Energy Loss (MWh)', val: dailySummary.total_energy_loss },
                { label: 'Peak Maximum Power (MW)', val: dailySummary.peak_maximum_power }
              ].map((m,i)=>(
                <div key={i}>
                  <p className="text-xl font-bold">{m.val ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Asset Table */}
      <Card>
        <CardHeader>
          <CardTitle>Asset List</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No assets found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium">Asset</th>
                    <th className="text-left py-3 px-4 font-medium">Type</th>
                    <th className="text-left py-3 px-4 font-medium">Make/Model</th>
                    <th className="text-left py-3 px-4 font-medium">Capacity</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-right py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssets.map((asset) => {
                    const Icon = asset.typeInfo.icon;
                    return (
                      <tr 
                        key={asset.asset_id} 
                        className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        onClick={() => handleAssetClick(asset)}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg bg-${asset.typeInfo.color}-100 dark:bg-${asset.typeInfo.color}-900/20`}>
                              <Icon className={`h-5 w-5 text-${asset.typeInfo.color}-600 dark:text-${asset.typeInfo.color}-400`} />
                            </div>
                            <div>
                              <p className="font-medium">{asset.display_name}</p>
                              <p className="text-sm text-gray-500">{asset.asset_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs bg-${asset.typeInfo.color}-100 text-${asset.typeInfo.color}-800 dark:bg-${asset.typeInfo.color}-900 dark:text-${asset.typeInfo.color}-200`}>
                            {asset.typeInfo.name}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-sm">{asset.make || '-'}</p>
                            <p className="text-xs text-gray-500">{asset.model || '-'}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-sm">{asset.capacity || '-'}</p>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(asset.status)}`}>
                            {asset.status}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssetClick(asset);
                            }}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Asset Health Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Asset Health Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Operational</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: `${(assets.filter(a => a.status === 'operational').length / assets.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {assets.filter(a => a.status === 'operational').length}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Warning</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full" 
                      style={{ width: `${(assets.filter(a => a.status === 'warning').length / assets.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {assets.filter(a => a.status === 'warning').length}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Critical</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${(assets.filter(a => a.status === 'critical').length / assets.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {assets.filter(a => a.status === 'critical').length}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded">
                  <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Inverter INV-001 High Temperature</p>
                  <p className="text-xs text-gray-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded">
                  <CloudRain className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Weather Sensor WS-002 Offline</p>
                  <p className="text-xs text-gray-500">5 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded">
                  <Activity className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Transformer TRAFO-001 Back Online</p>
                  <p className="text-xs text-gray-500">1 day ago</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Asset Performance Charts */}
      <Card>
        <CardHeader>
          <CardTitle>Chart Date Range</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Start Date</label>
              <DatePickerButton
                selectedDate={chartStartDate}
                onDateChange={setChartStartDate}
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Date</label>
              <DatePickerButton
                selectedDate={chartEndDate}
                onDateChange={setChartEndDate}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tab Navigation */}
      <div className="flex items-center space-x-3 mt-6">
        <Button variant={activeTab === 'inverter' ? 'default' : 'outline'} onClick={() => setActiveTab('inverter')}>
          Inverter
        </Button>
        <Button variant={activeTab === 'soiling' ? 'default' : 'outline'} onClick={() => setActiveTab('soiling')}>
          Soiling
        </Button>
      </div>

      {/* Tab Content */}
      {activeTab === 'inverter' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Inverter Efficiency Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <InverterEfficiencyHeatmap startDate={chartStartDate} endDate={chartEndDate} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Inverter Clipping Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <InverterClippingChart startDate={chartStartDate} endDate={chartEndDate} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Inverter Breakdown Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <InverterBreakdownChart startDate={chartStartDate} endDate={chartEndDate} />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Soiling Loss Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <SoilingLossChart startDate={chartStartDate} endDate={chartEndDate} />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AssetManagement;